import React, { memo, useCallback, useContext, useEffect, useMemo } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { useParams } from 'react-router-dom';

import {
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  RadioGroup,
  RadioGroupProps,
  Select,
  SelectProps,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react-v3';

import { logger, useGetUserInformation } from 'appSelectorContext';
import clsx from 'clsx';

import { Context, Types } from '../../store';
import { AppContext } from '../../store/appContext';
import {
  ActiveContainerType,
  ContainerResponse,
  ContainersSelection,
  containersType,
  ContainerType,
  Model,
  ReferenceQueryResponseItem,
} from '../../store/types';
import ScanInput from '../ScanInput';
import { isZhyRegion } from '../Utils/Utils';
import { DeleteContainer } from './DeleteContainer';
import { CONTAINER_RADIO_TYPES, CONTAINERS_ERROR_CODES_MAPPING } from './constants';
import useContainerSuggestions from './useContainerSuggestions';
import { useScanContainer } from './useScanContainer';
import { getBlockedSiteFromContainerId } from '../../constants/siteDeprecations';

import './scanContainer.scss';

export interface ContainerActiveState {
  activeContainer: ActiveContainerType | null;
  setActiveContainer: (value: ActiveContainerType | null) => void;
}

export interface ScanContainerProps extends ContainerActiveState {
  children?: React.ReactNode;
  setUpdatingContainer: (value: boolean) => void;
  processing: boolean;
  referenceId: string;
}

interface ContainerHeaderProps {
  containers: ActiveContainerType[];
  showHeader: boolean;
  disabled: boolean;
  onChange: () => void;
  onDelete: () => void;
}

interface SelectContainerProps extends ContainerActiveState {
  containers: ActiveContainerType[];
  disabled: boolean;
  show: boolean;
  onHandleClickAddNewContainer: () => void;
}

interface ManageContainerProps {
  add: boolean;
  edit: boolean;
  containers: ActiveContainerType[];
  inputVal: string;
  mpn: string;
  siteName: string;
  clientError: string | null;
  loading: boolean;
  containerSuggestionsLoading: boolean;
  onHandleScanContainer: (value: string) => void;
  onInputChange: (value: string) => void;
  onHandleCancel: () => void;
  containerRadioType: ContainerType;
  setContainerRadioType: (val: ContainerType) => void;
  containerSuggestions?: ContainerResponse[];
}

const ContainerHeader = memo(({ containers, disabled, onChange, onDelete, showHeader }: ContainerHeaderProps) => {
  if (containers.length === 0) {
    return null;
  }

  return (
    <Header
      variant="h3"
      description="Manage containers to move parts"
      counter={`(${containers.length})`}
      actions={
        showHeader ? (
          <SpaceBetween direction="horizontal" size="xs">
            <Box margin={{ top: 'xl' }}>
              <Button variant="link" iconName="edit" disabled={disabled} onClick={onChange}>
                Change container
              </Button>
            </Box>
            <DeleteContainer disabled={disabled} onDelete={onDelete} />
          </SpaceBetween>
        ) : null
      }
    >
      Containers
    </Header>
  );
});

const ScanNewContainer = memo(
  ({
    containers,
    show,
    disabled,
    onHandleClickAddNewContainer,
    setActiveContainer,
    activeContainer,
  }: SelectContainerProps) => {
    if (!show) {
      return null;
    }
    console.log(`selected container ${activeContainer?.value}`);
    return (
      <ColumnLayout columns={6}>
        <FormField label="Selected container" info={activeContainer?.containerType}>
          <Select
            data-testid="active-container"
            selectedOption={activeContainer}
            options={containers}
            onChange={({ detail }: { detail: SelectProps.ChangeDetail }) => {
              detail?.selectedOption && setActiveContainer(detail.selectedOption);
            }}
            selectedAriaLabel={`selected container ${activeContainer?.value}`}
            ariaLabel={`selected container ${activeContainer?.value}`}
            controlId="active-container"
          />
        </FormField>
        <Box margin={{ top: 'xl' }}>
          <Button variant="link" iconName="add-plus" disabled={disabled} onClick={onHandleClickAddNewContainer}>
            Scan new container
          </Button>
        </Box>
      </ColumnLayout>
    );
  },
);

const ManageContainer = memo(
  ({
    add,
    edit,
    containers,
    inputVal,
    clientError,
    loading,
    onHandleScanContainer,
    onInputChange,
    siteName,
    onHandleCancel,
    containerRadioType,
    setContainerRadioType,
    containerSuggestions = [],
    mpn,
    containerSuggestionsLoading,
  }: ManageContainerProps) => {
    const appContext = useContext(AppContext);
    const zhyRegion = isZhyRegion(appContext.appType);
    const scanContainerIdPromptText = 'Scan container Id to move part.';
    const placeholderText = edit ? 'Move existing parts to the new container.' : scanContainerIdPromptText;
    const descriptionText =
      !edit && zhyRegion
        ? placeholderText + ' Please manually verify container Id, as CMS is not yet regionalized in CN for validation.'
        : placeholderText;

    const conatinerSuggestionsOptions = useMemo(() => {
      if (containerSuggestionsLoading) {
        return [];
      }
      const mapToOption = (c: ContainerResponse) => ({
        value: c.containerName,
        label: `${c.containerName} ${c.quantity === 0 ? '' : `- (${c.quantity})`}`,
      });

      const EmptyBinOptions = [
        {
          value: '',
          label: 'No bins found',
        },
      ];
      const emptyContainers = containerSuggestions.filter((c: ContainerResponse) => c.quantity === 0).map(mapToOption);
      const otherContainers = containerSuggestions.filter((c: ContainerResponse) => c.quantity > 0).map(mapToOption);

      return [
        {
          label: `Bins with MPN ${mpn}`,
          options: containerSuggestions.length === 0 ? EmptyBinOptions : otherContainers,
        },
        {
          label: `Empty bins in ${siteName}`,
          options: containerSuggestions.length === 0 ? EmptyBinOptions : emptyContainers,
        },
      ];
    }, [containerSuggestions, containerSuggestionsLoading, siteName, mpn]);

    return (
      <div className={clsx('containers-section', { edit: edit, add, none: containers.length === 0 })}>
        <SpaceBetween size={'s'} direction={'horizontal'}>
          <FormField label={'Container type'}>
            <RadioGroup
              onChange={({ detail }: { detail: RadioGroupProps.ChangeDetail }) => {
                setContainerRadioType(detail.value);
              }}
              value={containerRadioType}
              items={CONTAINER_RADIO_TYPES}
            />
          </FormField>
          <ScanInput
            inputValue={inputVal}
            autoFocus
            placeholder={placeholderText}
            onScan={onHandleScanContainer}
            onChange={onInputChange}
            errors={clientError}
            label={edit ? 'Change Selected container' : 'Scan Container'}
            ariaLabel={edit ? 'Change Selected container' : 'Scan Container'}
            description={descriptionText}
            loading={loading}
            controlId="scan-container-input"
            autoSuggest={!zhyRegion}
            autoSuggestProps={{
              options: conatinerSuggestionsOptions,
              expandToViewport: true,
              virtualScroll: true,
              statusType: containerSuggestionsLoading ? 'loading' : 'finished',
              loadingText: 'Loading suggestions',
              empty: 'No bins found',
            }}
          />
          {(edit || add) && (
            <Box margin="l" padding="l">
              <Button onClick={onHandleCancel} disabled={loading}>
                Cancel
              </Button>
            </Box>
          )}
        </SpaceBetween>
      </div>
    );
  },
);

const ScanContainer = memo(
  ({
    activeContainer,
    setActiveContainer,
    setUpdatingContainer,
    processing = false,
    referenceId,
  }: ScanContainerProps) => {
    const { state, dispatch } = useContext(Context);
    const [edit, setEdit] = React.useState<boolean>(false);
    const [add, setAdd] = React.useState<boolean>(false);
    const [inputVal, setInputVal] = React.useState<string>('');
    const [clientError, setClientError] = React.useState<null | string>(null);
    const context = useGetUserInformation();
    const [containerRadioType, setContainerRadioType] = React.useState<ContainerType>(ContainerType.BIN);

    let { modelId }: any = useParams();
    const model: Model = state?.models?.find((m: Model) => m?.mpnIpnId === modelId && m?.referenceId === referenceId);

    const reference = state?.receiveReferences?.filter(
      (reference: ReferenceQueryResponseItem) => reference.receiveReference.receiveReferenceId === referenceId,
    )?.[0];
    const expectedMPN = model?.expectedMPN || '';

    const containerIds = useMemo(() => model?.containerIds || [], [model?.containerIds]);
    const containers = useMemo(
      () =>
        containerIds?.map((label: string) => {
          return { value: label, label: label };
        }),
      [containerIds],
    );

    const { loading: containerSuggestionsLoading, containerSuggestions } = useContainerSuggestions(
      model?.expectedMPN,
      model?.siteName,
      model && model?.containerIds?.length === 0,
    );

    const containersMap: containersType | ContainersSelection = state?.containers;

    const selectedContainer = activeContainer;
    const containerInfo =
      selectedContainer?.value && containersMap?.[selectedContainer?.value]
        ? containersMap?.[selectedContainer?.value]
        : null;

    const containerType = containerInfo?.type ? containerInfo?.type : containerInfo;
    const selectedContainerWithType = { ...selectedContainer, containerType };

    const onCompleted = (scannableContainerId: string) => {
      console.log('setActiveContainer', scannableContainerId);
      setActiveContainer({ value: scannableContainerId, label: scannableContainerId });
      setAdd(false);
      setEdit(false);
      setInputVal('');
      setContainerRadioType(ContainerType.BIN);
      setUpdatingContainer(false);
    };

    const { validateContainer, loading, error, reset } = useScanContainer(activeContainer?.value, onCompleted, edit);

    useEffect(() => {
      error && setClientError(ReactHtmlParser(CONTAINERS_ERROR_CODES_MAPPING[error]));
    }, [error, clientError]);

    useEffect(() => {
      if (!activeContainer?.value && containers.length > 0) {
        logger.info(
          `set active container from first item in containers list: ${JSON.stringify({
            container: containers[0],
            withUser: context.user,
          })} `,
        );
        setActiveContainer(containers[0]);
      }
    }, [containers, activeContainer, setActiveContainer, context]);

    const onHandleDeleteContainer = () => {
      dispatch({
        type: Types.deleteContainer,
        payload: {
          modelId,
          containerId: activeContainer?.value,
        },
      });
      logger.info(
        `handle delete container : ${JSON.stringify({
          payload: {
            modelId,
            containerId: activeContainer?.value,
          },
          withUser: context.user,
        })} `,
      );
      const filteredContainers = containers.filter((c) => c?.value !== activeContainer?.value);
      const container = filteredContainers.length > 0 ? filteredContainers[0] : null;
      setActiveContainer(container);
    };

    const onHandleChangeContainer = useCallback(() => {
      activeContainer?.value && setInputVal(activeContainer?.value);
      if (activeContainer?.value) {
        const changedContainerInfo =
          selectedContainer?.value && containersMap?.[selectedContainer?.value]
            ? containersMap?.[selectedContainer?.value]
            : null;
        const changedContainerType = changedContainerInfo?.type ?? containerType ?? ContainerType.BIN;
        setContainerRadioType(changedContainerType);
      }
      setEdit(true);
      setUpdatingContainer(true);
      reset();
    }, [activeContainer, selectedContainer, containersMap, reset, containerType, setUpdatingContainer]);

    const onHandleScanContainer = useCallback(
      (value: string) => {
        // validate if container belongs to a blocked/cutover site
        const blockedSite = getBlockedSiteFromContainerId(value);
        if (blockedSite) {
          setClientError(`This container belongs to site ${blockedSite} which has migrated to Boost. Use Boost to receive for this site.`);
          return;
        }
        // validate if duplicate containers
        const containerAlreadyScanned = containerIds.find((c: string) => c === value);
        if (containerAlreadyScanned) {
          setClientError('Duplicate container number scanned, container already exists!');
          return;
        }
        setClientError(null);
        validateContainer({
          variables: {
            scannableContainerId: value,
            containerType: containerRadioType,
            locationIdList: [reference?.receiveReference?.expectedReceiveLocation],
          },
        });
        logger.info(
          `handle scan container : ${JSON.stringify({
            variables: {
              scannableContainerId: value,
              containerType: containerRadioType,
              locationIdList: [reference?.receiveReference?.expectedReceiveLocation],
            },
            withUser: context.user,
          })} `,
        );
      },
      [containerIds, reference, containerRadioType, context, validateContainer],
    );

    const onHandleCancelManageContainer = useCallback(() => {
      setInputVal('');
      setEdit(false);
      setAdd(false);
      setUpdatingContainer(false);
      setClientError(null);
      setContainerRadioType(ContainerType.BIN);
      reset();
    }, [setUpdatingContainer, reset]);

    const onHandleClickAddContainer = useCallback(() => {
      setAdd(true);
      setInputVal('');
      setUpdatingContainer(true);
    }, [setUpdatingContainer]);

    const onHandleContainerNameChange = useCallback(
      (value: string) => {
        setInputVal(value);
        setClientError('');
        reset();
      },
      [reset],
    );

    return (
      <Container
        header={
          <ContainerHeader
            containers={containers}
            showHeader={containers?.length > 0 && !edit && !add}
            disabled={processing}
            onChange={onHandleChangeContainer}
            onDelete={onHandleDeleteContainer}
          />
        }
      >
        {containerSuggestionsLoading && <Spinner size="large" />}
        <ScanNewContainer
          show={containers?.length > 0 && !edit && !add && !containerSuggestionsLoading}
          containers={containers}
          disabled={processing}
          onHandleClickAddNewContainer={onHandleClickAddContainer}
          activeContainer={selectedContainerWithType}
          setActiveContainer={setActiveContainer}
        />

        {!containerSuggestionsLoading && (
          <ManageContainer
            add={add}
            edit={edit}
            mpn={expectedMPN}
            siteName={model?.siteName}
            containers={containers}
            inputVal={inputVal}
            clientError={clientError}
            loading={loading}
            onHandleScanContainer={onHandleScanContainer}
            onInputChange={onHandleContainerNameChange}
            onHandleCancel={onHandleCancelManageContainer}
            setContainerRadioType={setContainerRadioType}
            containerRadioType={containerRadioType}
            containerSuggestions={containerSuggestions}
            containerSuggestionsLoading={containerSuggestionsLoading || false}
          />
        )}
      </Container>
    );
  },
);

export default ScanContainer;
